import gql from 'graphql-tag';
import { useLazyQuery } from '@apollo/client';
import { Autocomplete, Text } from '@gasbuddy/react-components';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from '../../../lib/utils';

export const GET_LOCATION = gql`
  query locationsBySearchTerm($search: String!) {
    locationsBySearchTerm(search: $search) {
      displayName
    }
  }
`;

export default function CityAutocomplete({
  onChange,
  onSelect,
  value,
  ...rest
}) {
  const { t } = useTranslation('location');
  const [userInput, setUserInput] = useState(value);
  const [fetchedSearchTerm, setFetchedSearchTerm] = useState();
  const [locationsFilter, setLocationsFilter] = useState();
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const [fetchLocations, { error, data: locationData, loading: isFetching }] = useLazyQuery(GET_LOCATION);

  const getCitySearchResults = useCallback((searchTerm) => {
    if (shouldRefetch) {
      fetchLocations({
        variables: { search: searchTerm },
      });
      setFetchedSearchTerm(searchTerm);
      setLocationsFilter(); // reset filter because this is a new search
      setShouldRefetch(false);
    }
  }, [fetchLocations, shouldRefetch]);

  const handleChange = useCallback((searchTerm) => {
    setUserInput(searchTerm);
    onChange(searchTerm);

    if (searchTerm?.length > 2) {
      // check conditions where we don't need to refetch locations
      if (searchTerm.toLowerCase() === fetchedSearchTerm?.toLowerCase()) {
        // show original location list if new search term matches our most recent searched term
        setLocationsFilter();
      } else if (fetchedSearchTerm && searchTerm.toLowerCase().includes(fetchedSearchTerm.toLowerCase())) {
        // no need to refetch locations if new search term contains previous search term, just filter existing list
        setLocationsFilter(searchTerm);
      } else {
        setShouldRefetch(true);
      }
    } else {
      // clear out our fetched search term to clear our autocomplete options
      setFetchedSearchTerm();
    }
  }, [fetchedSearchTerm, onChange]);

  const handleSelectCity = useCallback(({ label: city }) => {
    setUserInput(city);
    onChange(city);
    onSelect(city);
  }, [onChange, onSelect]);

  // debounce city search calls when user input changes
  useEffect(() => {
    const typingTimer = setTimeout(() => getCitySearchResults(userInput), 500);
    return () => {
      if (typingTimer) {
        clearTimeout(typingTimer);
      }
    };
  }, [getCitySearchResults, userInput]);

  useEffect(() => {
    if (value) {
      setUserInput(value);
    }
  }, [value]);

  const citySuggestions = useMemo(() => (locationData?.locationsBySearchTerm || [])
    .filter(location => !locationsFilter || location.displayName.toLowerCase().includes(locationsFilter.toLowerCase()))
    .map(location => ({
      label: location.displayName,
      value: location.displayName,
    })), [locationData, locationsFilter]);

  return (
    <Fragment>
      <Autocomplete
        autoComplete="off"
        loading={isFetching}
        name="city"
        onChange={handleChange}
        onSelect={handleSelectCity}
        options={fetchedSearchTerm ? citySuggestions : []}
        value={userInput}
        {...rest}
      />
      {!isFetching && error && (
        <Text color="gold">{t('errorFetchingLocations')}</Text>
      )}
    </Fragment>
  );
}

CityAutocomplete.propTypes = {
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  value: PropTypes.string,
};

CityAutocomplete.defaultProps = {
  onChange: noop,
  onSelect: noop,
  value: '',
};
